/**
 * Admin operations + observability endpoints.
 *
 * Extracted from `src/services/api.ts` (Fase 10 Slice 10.8). Agrupa los
 * endpoints que la pestaña "Operaciones" del admin consume:
 *
 *   - GET  /admin/operations/live          — runtime controls + alerts
 *   - POST /admin/operations/toggles       — flip kill switches
 *   - GET  /admin/task-health              — cron freshness
 *   - GET  /admin/catalog/health           — Fase 2 catalog diagnostics
 *   - GET  /admin/operations/system-health — Fase 5 system snapshot
 *   - GET  /admin/config/history           — Fase 3 config history
 *   - POST /admin/config/{key}/rollback    — Fase 3 rollback
 *   - GET  /admin/audit-log                — Fase 4 audit log
 *   - GET  /admin/waitlist                 — country gate leads
 *   - GET  /admin/waitlist/metrics         — country gate analytics
 *
 * Re-exported from services/api.ts so existing call-sites keep working.
 */

import { apiClient } from '../client';
import type { RuntimeControlsConfig } from '../../types/config';

// ─── Operations live + toggles ──────────────────────────────────────────────

export interface IntegrationHealth {
  /** "ok" when configured / "missing" when env vars are blank.
   *  Future drill-in panels may add more states. */
  status: string;
  note: string;
}

export interface RecentAlert {
  event_type: string;
  severity: string;
  message: string;
  ts: number;
  fingerprint: string;
}

export interface OperationsLiveResponse {
  runtime_controls: RuntimeControlsConfig;
  shipping_rules_enabled: boolean;
  integration_health: Record<string, IntegrationHealth>;
  recent_alerts: RecentAlert[];
}

export const adminGetOperationsLive = async (): Promise<OperationsLiveResponse> => {
  const { data } = await apiClient.get<OperationsLiveResponse>('/admin/operations/live');
  return data;
};

export interface OperationsTogglesPayload {
  maintenance_enabled?: boolean;
  checkout_paused?: boolean;
  banner_enabled?: boolean;
  full_funnel_enabled?: boolean;
}

export const adminPostOperationsToggles = async (
  body: OperationsTogglesPayload,
): Promise<{ runtime_controls: RuntimeControlsConfig }> => {
  const { data } = await apiClient.post('/admin/operations/toggles', body);
  return data;
};

// ─── Shopify runtime ───────────────────────────────────────────────────────

export interface ShopifyRuntimeStatus {
  active_environment: 'production' | 'test';
  is_test: boolean;
  shop_url: string;
  expected_checkout_domain: string;
  /** Dominios de checkout de confianza editables desde el backoffice. */
  checkout_domains: string[];
  /** Dominios realmente aceptados por el guardrail (backoffice ∪ shop_url ∪ .env). */
  effective_checkout_domains: string[];
  /** GID del descuento automático Shopify (sync del % iniciación/mensual). */
  discount_gid: string;
  /** true si hay GID configurado → el % del backoffice se sincroniza a Shopify. */
  discount_sync_enabled: boolean;
  /** Texto del descuento de transición (placeholder {pct} = % aplicado). */
  discount_message_initiation: string;
  /** Texto del descuento de suscripción directa. */
  discount_message_monthly: string;
  storefront_token_configured: boolean;
  admin_token_configured: boolean;
  virtual_variant_configured: boolean;
  test_mapping_count: number;
  appstle_configured: boolean;
  test_checkout_ready: boolean;
  readiness_score: {
    ready: number;
    total: number;
  };
  checklist: Array<{
    key: string;
    label: string;
    ok: boolean;
    required_for_test_checkout: boolean;
    detail: string;
  }>;
  mapping_preview: Array<{
    production_variant_id: string;
    test_variant_id: string;
    test_product_id: string;
  }>;
  warnings: string[];
}

export interface ShopifyRuntimeValidation extends ShopifyRuntimeStatus {
  ok: boolean;
  validation_message: string;
  sample_product?: { id?: string; title?: string } | null;
}

export const adminGetShopifyRuntime = async (): Promise<ShopifyRuntimeStatus> => {
  const { data } = await apiClient.get<ShopifyRuntimeStatus>('/admin/operations/shopify-runtime');
  return data;
};

export interface ShopifyLocation {
  id: string;
  name: string;
}

export interface ShopifyLocationsResponse {
  /** false when Shopify can't be reached → caller degrades to storefront-only. */
  ok: boolean;
  locations: ShopifyLocation[];
  message?: string;
}

/** Lista las ubicaciones de Shopify para el selector de inventario.
 *  Nunca lanza por un Shopify caído: el backend devuelve ok=false con lista
 *  vacía y el caller restringe los modos a "Solo Storefront". */
export const adminGetShopifyLocations = async (): Promise<ShopifyLocationsResponse> => {
  const { data } = await apiClient.get<ShopifyLocationsResponse>('/admin/operations/shopify-locations');
  return data;
};

export const adminSetShopifyRuntime = async (
  active_environment: 'production' | 'test',
): Promise<ShopifyRuntimeStatus> => {
  const { data } = await apiClient.patch<ShopifyRuntimeStatus>('/admin/operations/shopify-runtime', {
    active_environment,
  });
  return data;
};

/** Guarda los dominios de checkout de confianza (custom domains de Shopify). */
export const adminSetShopifyCheckoutDomains = async (checkout_domains: string[]): Promise<ShopifyRuntimeStatus> => {
  const { data } = await apiClient.patch<ShopifyRuntimeStatus>('/admin/operations/shopify-runtime', {
    checkout_domains,
  });
  return data;
};

/** Guarda el GID del descuento automático Shopify y sincroniza los % al guardarlo. */
export const adminSetShopifyDiscountGid = async (discount_gid: string): Promise<ShopifyRuntimeStatus> => {
  const { data } = await apiClient.patch<ShopifyRuntimeStatus>('/admin/operations/shopify-runtime', {
    discount_gid,
  });
  return data;
};

/** Guarda los textos del descuento (transición/suscripción) y los sincroniza a Shopify. */
export const adminSetShopifyDiscountMessages = async (
  discount_message_initiation: string,
  discount_message_monthly: string,
): Promise<ShopifyRuntimeStatus> => {
  const { data } = await apiClient.patch<ShopifyRuntimeStatus>('/admin/operations/shopify-runtime', {
    discount_message_initiation,
    discount_message_monthly,
  });
  return data;
};

export const adminValidateShopifyRuntime = async (): Promise<ShopifyRuntimeValidation> => {
  const { data } = await apiClient.post<ShopifyRuntimeValidation>('/admin/operations/shopify-runtime/validate');
  return data;
};

// ─── PrestaShop runtime ──────────────────────────────────────────────────────

/** Read-only status of the PrestaShop environment. Never carries secrets:
 *  presence of the webservice/HMAC creds is reported as booleans only. */
export interface PrestashopRuntimeStatus {
  /** Public PrestaShop base URL (empty when unset). */
  url: string;
  /** true when both the webservice URL and key are set. */
  webservice_configured: boolean;
  /** true when the funnel HMAC secret is set. */
  funnel_hmac_configured: boolean;
  /** true when the PrestaShop checkout kill switch is on. */
  checkout_enabled: boolean;
  /** true when the PrestaShop checkout circuit breaker is OPEN. */
  checkout_breaker_open: boolean;
}

export const adminGetPrestashopRuntime = async (): Promise<PrestashopRuntimeStatus> => {
  const { data } = await apiClient.get<PrestashopRuntimeStatus>('/admin/operations/prestashop-runtime');
  return data;
};

// ─── Task health (cron freshness) ───────────────────────────────────────────

export interface TaskHealthResponse {
  tasks: Record<
    string,
    {
      last_success: number | null;
      seconds_ago: number | null;
      expected_interval_seconds: number | null;
      // True for tasks the operator can trigger on demand (renders a "run now" button).
      runnable?: boolean;
      // True for tasks whose cadence the operator can edit (frequency input).
      configurable?: boolean;
    }
  >;
}

export const adminGetTaskHealth = async (): Promise<TaskHealthResponse> => {
  const response = await apiClient.get<TaskHealthResponse>('/admin/task-health', {});
  return response.data;
};

export interface RunTaskResult {
  task: string;
  result: { ran: boolean; ok?: boolean; reason?: string };
}

/** Trigger a background sync on demand. Only whitelisted tasks are runnable. */
export const adminRunTask = async (taskName: string): Promise<RunTaskResult> => {
  const response = await apiClient.post<RunTaskResult>(`/admin/tasks/${taskName}/run`, {}, {});
  return response.data;
};

// ─── Catalog Health (Fase 2) ────────────────────────────────────────────────

export type CatalogIssueSeverity = 'critico' | 'atencion' | 'info';

export interface CatalogIssue {
  severity: CatalogIssueSeverity;
  type: string;
  message: string;
  product_id: string | null;
  name: string | null;
}

export interface CatalogHealthReport {
  status: 'ok' | 'atencion' | 'critico';
  issue_count: number;
  critical_count: number;
  issues: CatalogIssue[];
}

export const adminGetCatalogHealth = async (): Promise<CatalogHealthReport> => {
  const { data } = await apiClient.get<CatalogHealthReport>('/admin/catalog/health');
  return data;
};

// ─── System Health (Fase 5) ─────────────────────────────────────────────────

export type SystemHealthStatus = 'ok' | 'atencion' | 'critico' | 'desconocido';

/** A connection param an integration uses. Secrets arrive masked
 *  (value === 'configurado' when set), non-secrets verbatim. */
export interface BreakerConnectionParam {
  name: string;
  present: boolean;
  value: string;
}

export interface BreakerStatus {
  /** null for non-breaker integrations (config-only, e.g. Shopify app creds). */
  state: string | null;
  severity: 'ok' | 'atencion' | 'critico';
  failure_count: number | null;
  failure_threshold: number | null;
  configured: boolean;
  missing_env: string[];
  /** Connection params used for this integration's check, regardless of status. */
  params: BreakerConnectionParam[];
}

export interface SystemHealthSection {
  status: SystemHealthStatus;
  [key: string]: unknown;
}

export interface SystemHealthReport {
  status: SystemHealthStatus;
  checked_at: string;
  sections: {
    external: { status: SystemHealthStatus; breakers: Record<string, BreakerStatus> };
    catalog: { status: SystemHealthStatus; issue_count: number; critical_count: number };
    webhooks: {
      status: SystemHealthStatus;
      last_hour_count: number;
      last_24h_count: number;
      message: string | null;
    };
    leads: { status: SystemHealthStatus; failed_count_last_24h: number };
    substitutions: { status: SystemHealthStatus; pending_count: number | null };
  };
}

export const adminGetSystemHealth = async (): Promise<SystemHealthReport> => {
  const { data } = await apiClient.get<SystemHealthReport>('/admin/operations/system-health');
  return data;
};

// ─── Config History + Rollback (Fase 3) ─────────────────────────────────────

export interface ConfigHistoryEntry {
  id: string;
  key: string;
  old_value: unknown;
  new_value: unknown;
  changed_by: string | null;
  changed_at: string | null;
  reason: string | null;
  source: string | null;
}

export interface ConfigHistoryResponse {
  page: number;
  per_page: number;
  items: ConfigHistoryEntry[];
}

export const adminGetConfigHistory = async (params?: {
  key?: string;
  page?: number;
  per_page?: number;
}): Promise<ConfigHistoryResponse> => {
  const { data } = await apiClient.get<ConfigHistoryResponse>('/admin/config/history', {
    params: {
      key: params?.key || undefined,
      page: params?.page || 1,
      per_page: params?.per_page || 50,
    },
  });
  return data;
};

export interface ConfigRollbackResponse {
  status: 'ok';
  key: string;
  restored_to_history_id: string;
  new_history_id: string;
}

export const adminRollbackConfig = async (
  key: string,
  history_id: string,
  reason?: string,
): Promise<ConfigRollbackResponse> => {
  const { data } = await apiClient.post<ConfigRollbackResponse>(`/admin/config/${encodeURIComponent(key)}/rollback`, {
    history_id,
    reason,
  });
  return data;
};

// ─── Audit Log (Fase 4) ─────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: string;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before: unknown;
  after: unknown;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string | null;
}

export interface AuditLogResponse {
  total: number;
  page: number;
  per_page: number;
  items: AuditLogEntry[];
}

export const adminGetAuditLog = async (params?: {
  actor_email?: string;
  action?: string;
  entity_type?: string;
  entity_id?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}): Promise<AuditLogResponse> => {
  const { data } = await apiClient.get<AuditLogResponse>('/admin/audit-log', {
    params: {
      actor_email: params?.actor_email || undefined,
      action: params?.action || undefined,
      entity_type: params?.entity_type || undefined,
      entity_id: params?.entity_id || undefined,
      date_from: params?.date_from || undefined,
      date_to: params?.date_to || undefined,
      page: params?.page || 1,
      per_page: params?.per_page || 50,
    },
  });
  return data;
};

// ─── Waitlist (country gate) ────────────────────────────────────────────────

export interface WaitlistItem {
  id: string;
  email: string;
  pet_name: string | null;
  animal_type: string | null;
  pet_weight_kg: number | null;
  country: string | null;
  detected_country: string | null;
  /** Free-form origin marker set by each funnel touchpoint (may be null for
   *  country-gate leads, which carry no context). */
  context: string | null;
  /** Origin key derived from `context` by the backend: 'country' | 'salud-tier2'
   *  | 'otra-excluida' | 'no-compat' | 'otro'. */
  origin: string | null;
  created_at: string | null;
}

export interface WaitlistResponse {
  total: number;
  items: WaitlistItem[];
}

export const adminGetWaitlistLeads = async (params?: {
  limit?: number;
  offset?: number;
  country?: string;
  /** Business segment: 'country' | 'unservable'. */
  segment?: string;
  /** Fine origin key(s) within a segment (CSV allowed). */
  origin?: string;
}): Promise<WaitlistResponse> => {
  const response = await apiClient.get<WaitlistResponse>('/admin/waitlist', {
    params,
  });
  return response.data;
};

export interface WaitlistMetrics {
  total: number;
  leads_in_supported_countries: number;
  leads_in_unsupported_countries: number;
  supported_countries: string[];
  by_country: Record<string, number>;
  /** Lead count per origin key (see WaitlistItem.origin). */
  by_origin: Record<string, number>;
  by_month: Array<{ month: string; count: number }>;
}

export const adminGetWaitlistMetrics = async (months = 12, segment?: string): Promise<WaitlistMetrics> => {
  const response = await apiClient.get<WaitlistMetrics>('/admin/waitlist/metrics', {
    params: { months, ...(segment ? { segment } : {}) },
  });
  return response.data;
};
